import { ReleasingPriority } from 'data/pps/mockData';
import { makeAutoObservable, runInAction } from 'mobx';
import { getReleasingPriorityListService } from 'services/ReleasingPriorityListServices/getReleasingPriorityListService';
import { handleApiError } from 'utils/handleApiError';
import { toast } from 'react-toastify';

export class ReleasingPriorityStore {
  isLoading: boolean = false;
  isPolling: boolean = false;
  releasingPriorityData: ReleasingPriority[] = [];
  error: string | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchReleasingPriorityData = async () => {
    this.isLoading = true;
    try {
      const response = await getReleasingPriorityListService.getReleasingPriorityList();

      runInAction(() => {
        if (response.success) {
          this.releasingPriorityData = response.release_priority_list;
        } else {
          this.releasingPriorityData = [];
          this.error = response.message || 'Failed to fetch releasing priority data';
        }
      });
    } catch (error: unknown) {
      this.releasingPriorityData = [];
      this.error = error instanceof Error ? error.message : 'Unknown error';
      handleApiError(error, 'Error occurred while fetching releasing priority data');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollReleasingPriorityData = async () => {
    this.isPolling = true;
    try {
      const response = await getReleasingPriorityListService.getReleasingPriorityList();

      runInAction(() => {
        if (response.success) {
          // Update data only if it has changed
          if (
            JSON.stringify(this.releasingPriorityData) !==
            JSON.stringify(response.release_priority_list)
          ) {
            this.releasingPriorityData = response.release_priority_list;
          }
        } 
      });
    } catch (error: unknown) {
      // Don't show error for polling failures to avoid spam
      if(this.environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async () => {
    await this.fetchReleasingPriorityData();
  };
}
